/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, createContext, useCallback } from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";

export interface IFieldSectionContext {
	collapsible: boolean;
	isCollapsed: boolean;
	toggleCollapsed: () => void;
}

export const FieldSectionContext = createContext<IFieldSectionContext>(null);

export interface IFieldSectionProps {
	collapsible?: boolean;
	defaultCollapsed?: boolean;
	/** If section represents a main group - applies for section group, displays as big "main" group */
	isMain?: boolean;
	/** If section should be visually separated */
	isSeparated?: boolean;
	className?: string;
	/** Unique key to store collapsed state at */
	stateKey: string;
}

export const FieldSection: FC<IFieldSectionProps> = ({
	children,
	collapsible,
	defaultCollapsed,
	isMain,
	isSeparated,
	className,
	stateKey
}) => {
	const [ isCollapsed, setIsCollapsed ] = useLocalStorage(
		"adpt-editor-field-section_" + stateKey,
		defaultCollapsed ?? false
	);

	const toggleCollapsed = useCallback(() => {
		setIsCollapsed((x) => !x);
	}, []);

	const baseClass = BEM("edt-field-section", null, {
		"is-collapsible": collapsible,
		"is-collapsed": isCollapsed,
		"is-main": isMain,
		"is-separated": isSeparated
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<FieldSectionContext.Provider
				value={{
					collapsible: collapsible,
					isCollapsed: isCollapsed && collapsible,
					toggleCollapsed: toggleCollapsed
				}}
			>
				{children}
			</FieldSectionContext.Provider>
		</div>
	);
};
